import { computed } from 'vue'
import { useStore } from 'vuex'
import { i18n } from '@/translations/i18n'
import { showSnackbar } from '@/helper'

/** Success/error target from payments.proceed (and similar) payloads — backends differ. */
export function getProceedResponseRedirect(response) {
  if (!response || typeof response !== 'object') return null
  const nested = response.data && typeof response.data === 'object' ? response.data : null
  const candidates = [
    response.url,
    response.redirect,
    response.location,
    nested?.url,
    nested?.redirect,
    nested?.location,
  ]
  const url = candidates.find((u) => typeof u === 'string' && u.length > 0)
  return url ?? null
}

/** True when redirect points at checkout/purchase (or similar) with an Adyen/checkout error query. */
export function isProceedErrorRedirectUrl(url) {
  if (!url || typeof url !== 'string') return false
  let decoded = url
  try {
    decoded = decodeURIComponent(url)
  } catch {
    /* use raw */
  }
  if (/[?&#]err(?:=|%3D)/i.test(url) || /[?&#]err(?:=|%3D)/i.test(decoded)) return true
  return url.includes('err=') || decoded.includes('err=') || url.includes('err%3D')
}

export function isProceedErrorRedirect(response) {
  const url = getProceedResponseRedirect(response)
  return Boolean(url && isProceedErrorRedirectUrl(url))
}

class PaymentManager {
  constructor() {
    this.preInitHandler = null
    this.initHandler = null
    this.confirmHandler = null
  }

  register({ preInit, init, confirm }) {
    this.preInitHandler = typeof preInit === "function" ? preInit : null
    this.initHandler = typeof init === "function" ? init : null
    this.confirmHandler = typeof confirm === "function" ? confirm : null
  }

  clear() {
    this.active = null
  }

  preInit(payload) {
    if (!this.preInitHandler) {
      return
    }

    return this.preInitHandler(payload)
  }

  async init(payload) {
    if (!this.initHandler) {
      throw new Error("Init handler not registered")
    }

    return await this.initHandler(payload)
  }

  async confirm(payload) {
    if (!this.confirmHandler) {
      throw new Error("Confirm handler not registered")
    }

    return await this.confirmHandler(payload)
  }

  destroy() {
    this.preInitHandler = null
    this.initHandler = null
    this.confirmHandler = null
  }
}

export const paymentManager = new PaymentManager()

export function usePayment() {
  const store = useStore()
  const adyenConfig = computed(() => store.state.payment.adyenConfig)
  const paymentMethods = computed(() => store.state.payment.paymentMethods)
  const amazonPayConfig = computed(() => store.state.payment.amazonPayConfig)

  const isApplePaySupported = () => {
    return !!window.ApplePaySession
  }

  const availablePaymentMethods = computed(() => {
    return store.state.payment.paymentMethods.filter(method => {
      if (method.type === "applepay") {
        return isApplePaySupported()
      }

      return true
    })
  })

  const activePaymentMethod = computed({
    get: () => store.state.payment?.activePaymentMethod,
    set: (val) => store.dispatch('checkout/setPaymentMethod', val)
  })

  const getAdyenConfig = () => store.dispatch('payment/getAdyenConfig')
  const getPaymentMethods = () => store.dispatch('payment/getPaymentMethods')
  const getAmazonPayConfig = () => store.dispatch('payment/getAmazonPayConfig')
  const setPaymentMethod = (payload) => store.dispatch('checkout/setPaymentMethod', payload)
  const proceedToPayment = async (data = {}, redirectOnSuccess = true) => {
    const response = await store.dispatch("checkout/proceedToPayment", data)

    const refusedCodes = ['Refused', 'Cancelled', 'Error']
    const isRefused = response?.result?.resultCode && refusedCodes.includes(response.result.resultCode)
    const redirectUrl = getProceedResponseRedirect(response)
    const isErrorRedirect = Boolean(redirectUrl && isProceedErrorRedirectUrl(redirectUrl))
    const shouldRedirect = Boolean(redirectUrl && !isRefused && !isErrorRedirect)

    if (shouldRedirect) {
      if (redirectOnSuccess) {
        const absoluteUrl = redirectUrl.startsWith('http')
          ? redirectUrl
          : window.location.origin + redirectUrl

        window.location.assign(absoluteUrl)
      } else {
        return {
          success: true,
          url: redirectUrl
        }
      }
    }

    if (isErrorRedirect) {
      const msg =
        i18n.global?.t?.('messages.checkout.purchase.payment_refused') ||
        i18n.global?.t?.('messages.page.revocation.error') ||
        'Payment could not be completed.'
      showSnackbar('error', 'iconfont-info', msg)
    }

    return response
  }

  const getActivePaymentType = (paymentMethod) => paymentMethods.value.find(({ service_type }) => service_type === paymentMethod.service_type)?.type

  return {
    adyenConfig,
    paymentMethods,
    getAdyenConfig,
    amazonPayConfig,
    setPaymentMethod,
    proceedToPayment,
    getPaymentMethods,
    getAmazonPayConfig,
    activePaymentMethod,
    getActivePaymentType,
    availablePaymentMethods,
  }
}
